import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { fadeUp, viewportOnce } from "./motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREE_DOMAINS = [
  "gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "aol.com",
  "icloud.com", "protonmail.com", "proton.me", "mail.com", "gmx.com",
  "live.com", "msn.com", "yandex.com", "zoho.com", "me.com",
];

const inputCls =
  "w-full bg-[#0A0A0A] border border-zinc-700 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white transition-colors duration-300";

export const DataRoom = ({ open, setOpen }) => {
  const [form, setForm] = useState({ first_name: "", last_name: "", corporate_email: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = "Required";
    if (!form.last_name.trim()) errs.last_name = "Required";
    const email = form.corporate_email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errs.corporate_email = "Valid email required";
    } else if (FREE_DOMAINS.includes(email.split("@")[1].toLowerCase())) {
      errs.corporate_email = "Verified corporate domain required";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate() || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/data-room-request`, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        corporate_email: form.corporate_email.trim(),
      });
      toast.success("ACCESS REQUEST LOGGED", {
        description: `Reference ${data.reference_id.slice(0, 8).toUpperCase()} — compliance routing initiated.`,
      });
      setForm({ first_name: "", last_name: "", corporate_email: "" });
      setErrors({});
      setOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("TRANSMISSION REJECTED", {
        description: typeof detail === "string" ? detail : "Node unreachable. Retry.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <motion.div
            data-testid="data-room-cage-container"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            className="relative overflow-hidden bg-[#1A1A1A] border border-zinc-700 px-6 sm:px-12 py-20 lg:py-28 text-center"
          >
            <div className="relative">
              <p className="text-[10px] tracking-[0.35em] uppercase text-zinc-500 mb-8">
                03 / Private Data Room Cage
              </p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl uppercase tracking-tight font-bold text-white max-w-3xl mx-auto">
                Restricted Secure Data Room Access
              </h2>
              <p className="mt-6 text-xs sm:text-sm leading-relaxed text-zinc-400 max-w-xl mx-auto">
                Phase I technical allocation mappings, financial yield projections, and
                unblinded site coordinates are restricted to verified institutional
                counterparties.
              </p>
              <button
                data-testid="data-room-request-btn"
                onClick={() => setOpen(true)}
                className="mt-10 bg-white text-black font-display font-bold uppercase tracking-[0.15em] text-xs px-10 py-4 hover:bg-transparent hover:text-white border border-white transition-colors duration-300"
              >
                Request Secure Data Room Access
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="data-room-modal"
          className="bg-[#111111] border border-zinc-700 text-white rounded-none sm:max-w-md"
        >
          <DialogHeader>
            <p className="text-[9px] tracking-[0.35em] uppercase text-zinc-500 mb-2">
              Intake Node / Counterparty Verification
            </p>
            <DialogTitle className="font-display uppercase tracking-tight text-xl text-white">
              Request Data Room Access
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-400 leading-relaxed">
              Submissions route directly to compliance@equinoxih.com for domain
              verification and clearance review.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-4 mt-2" noValidate>
            <div>
              <label htmlFor="first-name" className="block text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">
                First Name
              </label>
              <input
                id="first-name"
                data-testid="input-first-name"
                value={form.first_name}
                onChange={update("first_name")}
                className={inputCls}
                placeholder="ALAN"
                autoComplete="given-name"
              />
              {errors.first_name && (
                <p data-testid="error-first-name" className="mt-1 text-[10px] uppercase tracking-widest text-red-400">
                  {errors.first_name}
                </p>
              )}
            </div>
            <div>
              <label htmlFor="last-name" className="block text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">
                Last Name
              </label>
              <input
                id="last-name"
                data-testid="input-last-name"
                value={form.last_name}
                onChange={update("last_name")}
                className={inputCls}
                placeholder="VANCE"
                autoComplete="family-name"
              />
              {errors.last_name && (
                <p data-testid="error-last-name" className="mt-1 text-[10px] uppercase tracking-widest text-red-400">
                  {errors.last_name}
                </p>
              )}
            </div>
            <div>
              <label htmlFor="corporate-email" className="block text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">
                Verified Corporate Email Domain
              </label>
              <input
                id="corporate-email"
                data-testid="input-corporate-email"
                type="email"
                value={form.corporate_email}
                onChange={update("corporate_email")}
                className={inputCls}
                placeholder="a.vance@yourfirm.com"
                autoComplete="email"
              />
              {errors.corporate_email && (
                <p data-testid="error-corporate-email" className="mt-1 text-[10px] uppercase tracking-widest text-red-400">
                  {errors.corporate_email}
                </p>
              )}
            </div>
            <button
              data-testid="submit-data-room-request-btn"
              type="submit"
              disabled={submitting}
              className="w-full mt-2 bg-white text-black font-display font-bold uppercase tracking-[0.15em] text-xs px-6 py-4 hover:bg-transparent hover:text-white border border-white transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Transmitting…" : "Transmit Access Request"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
